'use client';

import { motion, useReducedMotion } from 'framer-motion';

import { HeadlineWithImageSectionFragment } from '@/api';

interface RimacGroupStructureProps {
  section?: HeadlineWithImageSectionFragment | null;
}

export function RimacGroupStructure({ section }: RimacGroupStructureProps) {
  const anchor = section?.anchor || undefined;
  const button = section?.button;
  const reduceMotion = useReducedMotion();
  const reveal = (delay = 0) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: reduceMotion ? { duration: 0 } : { duration: 0.65, delay },
  });

  return (
    <section id={anchor} className="bg-white px-4 py-11 md:px-10 md:py-12">
      <div className="mx-auto flex max-w-[1360px] flex-col gap-11 md:gap-11">
        <motion.div
          {...reveal()}
          className="mx-auto flex max-w-[720px] flex-col gap-5 md:items-center md:gap-6 md:text-center"
        >
          <h2 className="text-black text-[40px] font-light leading-none tracking-[-2px] md:text-[48px] md:leading-[1.1] md:tracking-[-1.44px]">
            {section?.title || 'Rimac Group structure'}
          </h2>
          {section?.text && (
            <p className="text-base text-black leading-[1.4] tracking-[-0.32px]">
              {section.text}
            </p>
          )}
        </motion.div>

        <div className="flex flex-col gap-8">
          <motion.picture {...reveal(0.12)} className="block">
            <source
              media="(max-width: 767px)"
              type="image/svg+xml"
              srcSet="/images/structure/rimac-group-structure-mobile.svg"
            />
            <img
              src="/images/structure/rimac-group-structure-desktop.png"
              alt="Rimac Group structure: Bugatti Rimac, Rimac Technology, Rimac Energy and Verne"
              className="mx-auto block h-auto w-full"
            />
          </motion.picture>

          {button?.label && button?.path && (
            <motion.div
              {...reveal(0.22)}
              className="w-full md:mx-auto md:w-[325px]"
            >
              <a
                href={button.path}
                className="px-2.5 text-black flex h-[49px] w-full items-center justify-center rounded-[90px] bg-[#c1bab6] py-4 text-[12px] font-medium uppercase leading-[17px] tracking-[0.6px] transition-opacity hover:opacity-80"
              >
                {button.label}
              </a>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
